import { Box, styled, Typography } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { Avatar, Tooltip } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { getFullImageUrl } from 'utils'

const StyledBox = styled(Box)(
    ({ theme, selected, disabled, alreadyAdded }) => ({
        background: selected
            ? `${theme?.palette.primary.main} !important`
            : alreadyAdded || disabled
            ? '#f3f3f5 !important'
            : `${theme?.palette.common.white} !important`,
        padding: '0.8rem',
        display: 'flex',
        cursor: disabled || alreadyAdded ? 'default' : 'pointer',
        justifyContent: 'space-between',
        margin: '0 0 0.8rem 0',
        borderRadius: '0.4rem',
        minHeight: '4rem',
        scrollbarWidth: 'none',
        '&:hover': {
            border: `0.1rem solid ${theme?.palette?.primary.main}`,
            background: selected
                ? `${theme?.palette.tertiary.gradient} !important`
                : alreadyAdded || disabled
                ? '#f3f3f5 !important'
                : '#efeff1 !important',
            '& .MuiBox-root': {
                background: selected
                    ? 'transparent !important'
                    : alreadyAdded || disabled
                    ? '#f3f3f5 !important'
                    : '#efeff1 !important',
            },
        },
        '& .MuiBox-root': {
            background: selected
                ? `${theme?.palette.primary.main} !important`
                : alreadyAdded || disabled
                ? '#f3f3f5 !important'
                : `${theme?.palette.common.white} !important`,
            maxWidth: 'fit-content !important',
        },
        '& span': {
            fontSize: '1.2rem',
            color: selected
                ? `${theme?.palette.common.white} !important`
                : `${theme?.palette.grey['900']} !important`,
            width: '17.5rem',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            display: 'block',
        },
        '& p': {
            color: selected
                ? `${theme?.palette.common.white} !important`
                : `${theme?.palette.grey['900']} !important`,
        },
        '& h2': {
            fontSize: '1.4rem !important',
            color: selected
                ? `${theme?.palette.common.white} !important`
                : disabled
                ? '#8b909a !important'
                : '#3b454d !important',
            fontWeight: 'normal',
            width: '19rem',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            fontFamily: 'Rubik',
        },
        '& svg': {
            width: '1.12rem',
            height: '1.12rem',
        },
        '& .MuiAvatar-circular': {
            width: '1.6rem !important',
            height: '1.6rem !important',
            borderRadius: '50%',
            marginRight: '0.8rem',
        },
    }),
)

const AssignmentItem = ({
    item,
    checkIfSelected,
    isAlreadyMoved = false,
    handleSelectItem,
    handleDoubleClick = () => {
        return
    },
    showCrossIcon = false,
    showPlusIcon = false,
    imageThumbUrl,
}) => {
    const { t } = useTranslation()

    const { primaryText, secondaryText, key } = item
    const isSelected = checkIfSelected(item)

    const handleItemKeyDown = (event) => {
        if (
            !isAlreadyMoved &&
            (event.key === 'Enter' || event.key === ' ')
        ) {
            event.preventDefault()
            handleSelectItem(item)
        }
    }

    return (
        <>
            <StyledBox
                style={{ userSelect: 'none' }}
                selected={isSelected}
                alreadyAdded={isAlreadyMoved}
                disabled={isAlreadyMoved}
                key={`search_item-${key}`}
                role="option"
                tabIndex={isAlreadyMoved ? -1 : 0}
                aria-selected={isSelected}
                aria-disabled={isAlreadyMoved}
                data-ai-explain={
                    isAlreadyMoved
                        ? `Application already assigned: ${primaryText}`
                        : isSelected
                        ? `Selected application: ${primaryText}`
                        : `Select application: ${primaryText}`
                }
                onClick={() => {
                    if (!isAlreadyMoved) {
                        handleSelectItem(item)
                    }
                }}
                onKeyDown={handleItemKeyDown}
                onDoubleClick={(e) => {
                    if (!isAlreadyMoved) {
                        handleDoubleClick(e, item)
                    }
                }}
            >
                <div
                    style={{
                        maxWidth: '19rem',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Tooltip
                        title={
                            <>
                                <Typography
                                    style={{
                                        fontSize: '1.4rem',
                                        fontWeight: 'normal',
                                    }}
                                >
                                    {primaryText}
                                </Typography>
                                {secondaryText && (
                                    <Typography
                                        style={{
                                            fontSize: '1.2rem',
                                        }}
                                    >
                                        {secondaryText}
                                    </Typography>
                                )}
                            </>
                        }
                    >
                        <Box>
                            <Box display={'flex'}>
                                <Avatar
                                    size={'small'}
                                    src={getFullImageUrl(imageThumbUrl)}
                                />

                                <Typography variant="h2">
                                    {primaryText}
                                </Typography>
                            </Box>
                            {secondaryText}
                        </Box>
                    </Tooltip>
                </div>
                <Box>
                    {showCrossIcon ? (
                        <>
                            <Icon
                                name="SimpleUncheck"
                                color={isSelected ? '#fff' : 'red'}
                            />
                        </>
                    ) : (
                        <>
                            {isAlreadyMoved && (
                                <Icon
                                    name="Check"
                                    color={isAlreadyMoved ? '#8b909a' : '#fff'}
                                />
                            )}
                            {showPlusIcon && (
                                <Icon
                                    name="Plus"
                                    style={{ width: '2rem', height: '2rem' }}
                                    color={
                                        isSelected ? '#fff' : 'green'
                                    }
                                />
                            )}
                        </>
                    )}
                </Box>
            </StyledBox>
        </>
    )
}

export default AssignmentItem
